<script setup>
import { computed, useAttrs } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useDisplaySenderName } from '../useDisplaySenderName.js';
import { isGroupConversation } from '../groupParticipant.js';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
  hideSenderName: { type: Boolean, default: false },
  showSenderName: { type: Boolean, default: false },
  mediaCaption: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  sender,
  contentAttributes,
  conversationId,
} = useMessageContext();

const attrs = useAttrs();

const conversationGetter = useMapGetter('getConversationById');

// Aceleres: a ponte WAME marca o grupo em `custom_attributes` do contato, mas
// esta checagem só olhava `additional_attributes` — que vem vazio. Com isso a
// conversa de grupo era classificada como individual e o nome do remetente
// nunca era exibido, deixando a autoria de cada fala só no prefixo cravado no
// texto. A resolução agora vive em groupParticipant.js, junto do resto da
// identidade de grupo.
const isWhatsAppGroup = computed(() =>
  isGroupConversation(conversationGetter.value(conversationId.value))
);

// Aceleres: a resolução vive em useDisplaySenderName para que toda bolha use a
// mesma regra — inclusive o álbum de stickers, que monta o próprio rótulo.
const displaySenderName = useDisplaySenderName();

// O nome exibido pode vir do participante do grupo, e não do `sender` — em
// grupo o `sender` é o contato do grupo inteiro. Exigir `sender.name` aqui
// esconderia o rótulo justamente quando ele é mais necessário.
const shouldShowSenderName = computed(
  () =>
    (sender.value?.name || displaySenderName.value) &&
    (props.showSenderName ||
      (isWhatsAppGroup.value && orientation.value === ORIENTATION.LEFT))
);

// Aceleres: o nome do remetente é o título da bolha (aparece acima de
// imagem, vídeo e documento), então usa 16,2px nos dois sentidos da
// conversa. O corpo de texto — mensagem e legenda — fica em 15,8px, em
// `.prose-bubble p` e na regra das legendas (aceleres-overrides.css).
const senderNameClass = computed(() => {
  if (!props.showSenderName || orientation.value !== ORIENTATION.RIGHT) {
    return 'text-[16.2px] font-semibold';
  }

  return 'relative left-1.5 right-auto px-[5px] pt-0 pb-[5px] !text-[16.2px] !font-bold';
});

const groupSenderColor = computed(() => {
  const colors = [
    'text-[#e05d32]',
    'text-[#ba33a3]',
    'text-[#1f8f78]',
    'text-[#4e63b6]',
    'text-[#a56b16]',
    'text-[#b54e63]',
  ];
  const name = displaySenderName.value || '';

  if (props.showSenderName) return '!text-[#000]';
  if (!isWhatsAppGroup.value) return 'text-n-slate-11';

  // Some o comprimento do nome escolhia a cor, então quaisquer dois
  // participantes com nomes do mesmo tamanho ("Bruno"/"Diego") saíam na mesma
  // cor — o oposto do que a cor existe para fazer aqui. Um hash sobre os
  // caracteres distribui bem melhor e continua estável para o mesmo nome.
  const hash = Array.from(name).reduce(
    (acc, char) => (acc * 31 + char.codePointAt(0)) % 100003,
    7
  );

  return colors[hash % colors.length];
});

// Aceleres: marcador "Encaminhada" no topo do bubble (estilo WhatsApp Web).
// O backend (messages_controller.rb#forward) seta content_attributes.forwarded
// = true ao criar a mensagem nova no destino. Como Base.vue envolve TODAS as
// bolhas (texto/imagem/arquivo/áudio/vídeo), o label aparece em qualquer tipo.
const isForwarded = computed(() => !!contentAttributes.value?.forwarded);

const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-[#d9fdd3] text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    '!bg-n-solid-amber text-n-amber-12 shadow-sm ring-1 ring-inset ring-n-amber-7/50 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-white text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]: 'left-bubble rounded-xl',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

// Aceleres: sticker tem fundo transparente e o MessageMeta (horario) e
// sobreposto por cima da propria imagem — ver Image.vue, que emite este
// mesmo `data-bubble-name`. Usado abaixo pra colar o horario na quina do
// sticker (ver comentario no template) e no CSS de espacamento entre
// stickers (Message.vue, seletor `[data-bubble-name='sticker']`).
const isStickerBubble = computed(() => attrs['data-bubble-name'] === 'sticker');

// Aceleres: no documento (attachment) o horario nao fica sobreposto ao
// fundo tingido do painel (icone+nome+tipo) — flui como linha propria
// abaixo dele, fora do .acl-file-bubble__body (ver File.vue).
const isFileBubble = computed(() => attrs['data-bubble-name'] === 'attachment');

const textBubbleClass = computed(() => {
  if (attrs['data-bubble-name'] !== 'text') return [];

  if (orientation.value === ORIENTATION.LEFT) {
    return [
      "[&.left-bubble[data-bubble-name='text'].acl-text-bubble]:!p-[6px_0_6px_8px]",
    ];
  }

  if (orientation.value === ORIENTATION.RIGHT) {
    return [
      "[&.right-bubble[data-bubble-name='text'].acl-text-bubble]:!p-[6px_15px_6px_8px]",
    ];
  }

  return [];
});

// Aceleres: clique no preview de reply-to faz scroll/highlight da mensagem
// original (estilo WhatsApp: tap no quote → vai pra mensagem citada).
const scrollToMessage = event => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
  if (!inReplyTo?.value?.id) return;
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () => !props.hideMeta && variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// Espelha o palette do <Avatar /> (text colors do tuple light), indexado
// por `name.length % 6` — mesma lógica de `getColorsByNameLength`. Assim a
// borda do bloco de reply combina com o tom saturado do avatar da pessoa
// cuja mensagem está sendo citada.
const AVATAR_BORDER_COLORS = [
  '#C2298A',
  '#99543A',
  '#60646C',
  '#008573',
  '#4747C2',
  '#3A5BC7',
];

const replyToBorderColor = computed(() => {
  const name = inReplyTo?.value?.sender?.name;
  if (!name) return AVATAR_BORDER_COLORS[2];
  return AVATAR_BORDER_COLORS[name.length % AVATAR_BORDER_COLORS.length];
});
</script>

<template>
  <div
    v-twemoji
    class="text-[14px] leading-[19px] [&_.cw-emoji]:inline-block [&_.cw-emoji--with-gap]:mr-px"
    :class="[
      messageClass,
      textBubbleClass,
      {
        'min-w-[4.5rem] max-w-[min(85vw,42rem)] sm:max-w-[min(65vw,42rem)]':
          variant !== MESSAGE_VARIANTS.EMAIL,
        relative: variant !== MESSAGE_VARIANTS.EMAIL && shouldShowMeta,
        'pb-4':
          variant !== MESSAGE_VARIANTS.EMAIL &&
          shouldShowMeta &&
          !props.mediaCaption,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="-mx-0.5 mb-1.5 cursor-pointer rounded-md border-l-4 bg-[rgba(194,189,184,0.15)] px-2 py-1.5 acl-reply-to-preview"
      :style="{ borderLeftColor: replyToBorderColor }"
      role="button"
      tabindex="0"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        v-twemoji
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <div
      v-if="shouldShowSenderName && !hideSenderName"
      class="mb-1 leading-tight opacity-90"
      :class="[
        'acl-sender-name',
        groupSenderColor,
        senderNameClass,
        { 'acl-media-sender': props.showSenderName },
      ]"
    >
      {{ displaySenderName }}
    </div>
    <!-- Aceleres: rótulo "Encaminhada" no topo do bubble, igual WhatsApp Web.
         Verde-WhatsApp, com ícone de seta dupla de encaminhamento. Aparece
         antes do conteúdo em qualquer tipo de mensagem. -->
    <div
      v-if="isForwarded"
      class="mb-1 flex items-center gap-1 text-[11px] font-medium leading-tight text-n-slate-11/80 acl-forwarded-label"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        style="flex-shrink: 0"
        aria-hidden="true"
      >
        <polyline points="15 14 20 9 15 4" />
        <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
      </svg>
      <span>Encaminhada</span>
    </div>
    <div
      v-if="variant === MESSAGE_VARIANTS.PRIVATE"
      class="mb-2 flex items-center gap-1.5 border-b border-n-amber-7/60 pb-2 text-[11px] font-bold uppercase tracking-wide text-n-amber-12"
    >
      <span class="i-ph-note-pencil size-3.5" aria-hidden="true" />
      <span>{{ t('CONVERSATION.REPLYBOX.INTERNAL_NOTE_BADGE') }}</span>
      <span
        class="ml-auto font-medium normal-case tracking-normal text-n-amber-11"
      >
        {{ t('CONVERSATION.REPLYBOX.TEAM_ONLY') }}
      </span>
    </div>
    <div
      :class="
        shouldShowMeta &&
        variant !== MESSAGE_VARIANTS.EMAIL &&
        !props.mediaCaption
          ? 'pr-12 mb-2.5'
          : ''
      "
      class="min-w-0 break-words"
    >
      <slot />
    </div>
    <!-- Aceleres: no sticker o horario cola na quina inferior (bottom-0) em
         vez do bottom-2.5 padrao das outras midias — o proprio sticker ja
         costuma trazer respiro transparente embutido no webp, e colar o
         horario na borda real e o que deixa o espacamento de 12px entre
         stickers (Message.vue) contado a partir do horario batendo certo,
         sem sobrepor a bolha seguinte. So se aplica ao sticker: foto/video
         mantem o bottom-2.5 de sempre. -->
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        'justify-end',
        variant === MESSAGE_VARIANTS.EMAIL
          ? 'mt-2 px-3 pb-3'
          : isFileBubble
            ? ['px-2', 'pt-2', 'pb-2']
            : [
                'absolute',
                'right-2',
                isStickerBubble ? 'bottom-0' : 'bottom-2.5',
              ],
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
    />
  </div>
</template>
